import {
  CalendarDays,
  Home,
  LayoutGrid,
  Maximize2,
  Menu,
  Minimize2,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bracket,
  BracketMatchCard,
  COMPETITION_CATEGORY_LABELS,
  COMPETITION_STATUS_LABELS,
  COMPETITION_STATUS_TONE,
  GroupStandingsCard,
  LiveMatchCard,
  MatchScoreRow,
  MATCH_TYPE_LABELS,
  PUBLIC_SCREEN_REFRESH_MS,
  WinnerCard,
  buildGroupTable,
  buildPublicNameMap,
  championLabel,
  classificationGroupsView,
  currentRoundLabel,
  extraQualifierCaptains,
  finalGroupView,
  formatDateRange,
  groupChampionLabel,
  groupsFromMatches,
  matchHasPair,
  podiumEntries,
  PodiumCard,
  roundAccent,
  roundLabel,
  screenProgress,
  shortTime,
  splitScreenMatches,
  toBracketMatchView,
  UpcomingMatchMeta,
  toBracketView,
  todayIso,
  usePublicBracketQuery,
  usePublicBracketsBatchQuery,
  usePublicClubCompetitionsQuery,
  usePublicCompetitionInfoQuery,
  usePublicCourtScheduleQuery,
  usePublicMatchesBatchQuery,
  usePublicMatchesQuery,
  usePublicStandingsBatchQuery,
  usePublicStandingsQuery,
  type PublicCompetitionSummary,
} from '@/features/competition';
import socialFacebook from '@/assets/landing/social-facebook.svg';
import socialInstagram from '@/assets/landing/social-instagram.svg';
import socialTiktok from '@/assets/landing/social-tiktok.svg';
import { publicScreenConfig } from '@/shared/config/publicScreens';
import { PublicClubOverview } from './public/PublicClubOverview';
import { initPublicScreenAnalytics } from '@/shared/lib/analytics';
import { readStoredWideMode, storeWideMode } from '@/shared/lib/publicScreenLayout';
import { cn } from '@/shared/lib/cn';
import {
  Alert,
  Avatar,
  Badge,
  Card,
  CardHeader,
  EmptyState,
  IconButton,
  Logo,
  MetaBar,
  ProgressBar,
  Skeleton,
} from '@/shared/ui';

/** Rozstawienia nie ma w publicznym API — drabinka rysuje się bez numerów. */
const NO_SEEDS: ReadonlyMap<string, number> = new Map();

/** Wartosc wyboru oznaczajaca zakladke „Info ogólne" (domyslna po wejsciu). */
const INFO_TAB = 'info';

export interface PublicTournamentScreenPageProps {
  /** Skrót klubu z adresu, np. `as` dla Akademii Sportu. */
  slug: string;
}

/**
 * Publiczny ekran turnieju (tryb prezentacji) pod `/turnieje/<skrót klubu>`.
 *
 * Widok czysto odczytowy dla niezalogowanych: przełącznik publicznych rozgrywek
 * klubu, a pod nim centrum turnieju — drabinka, status kortów, mecze w trakcie,
 * następne i ostatnie wyniki. Świadomie nie ma tu żadnego wejścia do logowania
 * ani akcji zmieniających stan; wszystkie dane idą przez `/api/public/**`,
 * które serwuje wyłącznie rozgrywki z widocznością PUBLIC.
 */
export function PublicTournamentScreenPage({ slug }: PublicTournamentScreenPageProps) {
  const { t } = useTranslation('tournament');
  const screen = publicScreenConfig(slug);
  const clubId = screen?.clubId ?? null;
  // „info" = zakladka Info ogólne (domyslna); inaczej id wybranego turnieju.
  const [pickedId, setPickedId] = useState<string>(INFO_TAB);
  /** Szuflada z listą turniejów — dotyczy wyłącznie wąskich ekranów. */
  const [menuOpen, setMenuOpen] = useState(false);
  /**
   * Tryb pelnoekranowy — tresc idzie na cala szerokosc zamiast w kolumne
   * `max-w-[110rem]`. Stan startowy czytamy z `localStorage` leniwym
   * inicjalizatorem, zeby ustawienie przetrwalo przeladowanie bez mrugniecia
   * ukladem (efekt po pierwszym renderze przestawialby szerokosc na oczach).
   */
  const [wideMode, setWideMode] = useState(readStoredWideMode);

  /** Przelacza szerokosc i od razu utrwala wybor. */
  function toggleWideMode() {
    setWideMode((previous) => {
      const next = !previous;
      storeWideMode(next);
      return next;
    });
  }

  // Kolumna tresci: domyslnie ograniczona, w trybie pelnoekranowym cala szerokosc.
  const containerWidth = wideMode ? 'max-w-none' : 'max-w-[110rem]';

  // Google Analytics tylko na ekranie publicznym — zalogowana czesc aplikacji
  // nie jest sledzona, wiec tagu nie ma w index.html.
  useEffect(() => {
    initPublicScreenAnalytics();
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  const clubQuery = usePublicClubCompetitionsQuery(clubId ?? '');
  const competitions = useMemo(() => clubQuery.data?.competitions ?? [], [clubQuery.data]);

  // Przelacznik po lewej pokazuje przy kazdym turnieju postep i mistrza —
  // mecze wszystkich rozgrywek i drabinki zakonczonych ida zapytaniami zbiorczymi
  // (te same klucze cache co zakladka „Info ogólne", wiec nic nie dubluje ruchu).
  const competitionIds = useMemo(() => competitions.map((item) => item.id), [competitions]);
  // Drabinki maja tylko systemy pucharowe; liga i formaty grupowe koncza sie
  // tabela — ich mistrza czytamy z tabel (grupa finalowa/podium albo tabela ligi).
  const completedBracketIds = useMemo(
    () =>
      competitions
        .filter(
          (item) =>
            item.status === 'COMPLETED' &&
            (item.system === 'PLAYOFF' || item.system === 'GROUP_PLAYOFF'),
        )
        .map((item) => item.id),
    [competitions],
  );
  const completedTableIds = useMemo(
    () =>
      competitions
        .filter((item) => item.status === 'COMPLETED' && item.system !== 'PLAYOFF')
        .map((item) => item.id),
    [competitions],
  );
  const switcherMatches = usePublicMatchesBatchQuery(competitionIds);
  const completedBrackets = usePublicBracketsBatchQuery(completedBracketIds);
  const completedStandings = usePublicStandingsBatchQuery(completedTableIds);
  const switcherChampions = useMemo(() => {
    const byCompetition = new Map<string, string>();
    for (const [competitionId, bracket] of completedBrackets.byId) {
      const bracketNames = buildPublicNameMap(bracket.players);
      const label = championLabel(toBracketView(bracket.bracket, bracketNames, NO_SEEDS));
      if (label) byCompetition.set(competitionId, label);
    }
    // Turnieje bez drabinki: mistrz z tabeli grupy finalowej / grupy o podium
    // (kubelek 0), a dla ligi z jedynej tabeli (bez numeru grupy).
    for (const [competitionId, data] of completedStandings.byId) {
      if (byCompetition.has(competitionId)) continue;
      const tableNames = buildPublicNameMap(data.players);
      const label =
        groupChampionLabel(data.standings, tableNames, 0) ??
        groupChampionLabel(data.standings, tableNames, null);
      if (label) byCompetition.set(competitionId, label);
    }
    return byCompetition;
  }, [completedBrackets.byId, completedStandings.byId]);

  /**
   * Plan następnych meczów pokazujemy dopiero po naciśnięciu „Upublicznij”
   * w zakładce Infrastruktura. Do tego czasu sekcji po prostu nie ma — backend
   * i tak nie wydaje wtedy terminów meczów, które się jeszcze nie zaczęły.
   */
  const schedulePublished = clubQuery.data?.schedulePublished ?? false;

  // Wybór trzymamy tylko wtedy, gdy nadal istnieje na odświeżonej liście —
  // inaczej ekran wraca do zakladki „Info ogólne".
  const activeId =
    pickedId !== INFO_TAB && competitions.some((item) => item.id === pickedId) ? pickedId : null;

  const infoQuery = usePublicCompetitionInfoQuery(activeId ?? undefined);
  const info = infoQuery.data;
  const matchesQuery = usePublicMatchesQuery(activeId ?? undefined);
  const bracketQuery = usePublicBracketQuery(
    activeId ?? undefined,
    info !== undefined && info.bracketSize !== null,
  );
  const courtsQuery = usePublicCourtScheduleQuery(activeId ?? undefined, todayIso());

  const matches = useMemo(() => matchesQuery.data?.matches ?? [], [matchesQuery.data]);

  // Tabele mają sens dopiero po losowaniu — wcześniej nie istnieją, a ekran
  // odpytywałby o nie co 30 sekund. Ligi (SINGLE_GROUP/CHALLENGE) mają jedną
  // tabelę bez grup, więc standings dotyczą ich zawsze.
  const hasGroupMatches = matches.some((match) => match.groupNumber !== null);
  const isLeague = info?.system === 'SINGLE_GROUP' || info?.system === 'CHALLENGE';
  const standingsQuery = usePublicStandingsQuery(
    activeId ?? undefined,
    hasGroupMatches || isLeague,
  );
  const standings = useMemo(() => standingsQuery.data?.standings ?? [], [standingsQuery.data]);

  // Jeden słownik nazwisk na wszystkie sekcje — backend dokleja go do każdej
  // odpowiedzi, bo anonimowy klient nie ma prawa odpytać user-service.
  const names = useMemo(() => {
    const merged = new Map<string, string>();
    for (const players of [
      matchesQuery.data?.players,
      bracketQuery.data?.players,
      courtsQuery.data?.players,
      standingsQuery.data?.players,
    ]) {
      if (!players) continue;
      for (const [id, name] of buildPublicNameMap(players)) merged.set(id, name);
    }
    return merged;
  }, [matchesQuery.data, bracketQuery.data, courtsQuery.data, standingsQuery.data]);

  const bracketView = useMemo(
    () => (bracketQuery.data ? toBracketView(bracketQuery.data.bracket, names, NO_SEEDS) : null),
    [bracketQuery.data, names],
  );
  const sections = useMemo(() => splitScreenMatches(matches), [matches]);
  const progress = useMemo(() => screenProgress(matches), [matches]);

  // Skład grup czytamy z meczów, nie ze zgłoszeń — publiczne API ich nie wydaje.
  const groups = useMemo(() => groupsFromMatches(matches, names), [matches, names]);
  // Aktualni posiadacze dodatkowych miejsc awansu („awans z dalszych miejsc") —
  // ta sama projekcja na żywo co w panelu organizatora.
  const extraCaptains = useMemo(
    () =>
      extraQualifierCaptains(
        standings,
        info?.advancingPerGroup ?? null,
        info?.extraQualifiers ?? null,
      ),
    [standings, info?.advancingPerGroup, info?.extraQualifiers],
  );
  const finalGroup = useMemo(() => finalGroupView(matches, names), [matches, names]);
  // Grupy klasyfikacyjne (kubełek 0 + ujemne) — tryb rozpoznajemy po samych meczach,
  // bo publiczne DTO nie niesie secondPhaseType. Więcej niż jeden kubełek = klasyfikacja.
  const classificationGroups = useMemo(
    () => classificationGroupsView(matches, names),
    [matches, names],
  );
  const isClassification = classificationGroups.length > 1;

  // Tabela ligi — jeden ranking (groupNumber === null) dla SINGLE_GROUP i CHALLENGE;
  // skład bierzemy wprost ze standings, bo mecze ligi nie niosą numeru grupy.
  const leagueRows = useMemo(() => {
    if (!isLeague) return [];
    const members = standings
      .filter((standing) => standing.groupNumber === null)
      .map((standing) => ({
        ids: [standing.userId, ...(standing.partnerId ? [standing.partnerId] : [])],
        label: '',
        seed: null,
      }));
    return buildGroupTable(null, members, standings, names, null);
  }, [isLeague, standings, names]);
  const groupMatchesLeft = groups.reduce(
    (left, group) => left + (group.matchesTotal - group.matchesResolved),
    0,
  );

  const courtNames = useMemo(
    () =>
      new Map(
        (courtsQuery.data?.courts ?? []).map((court) => [
          court.courtId,
          court.name || t('publicScreen.court', { number: court.number }),
        ]),
      ),
    [courtsQuery.data, t],
  );

  /** Plan kortów zostaje źródłem nazw kortów przy meczach — sam status kortów już się nie wyświetla. */
  const courtCount = courtsQuery.data?.courts.length ?? 0;

  const champion = bracketView ? championLabel(bracketView) : null;
  // Podium turnieju (miejsca 1-3) — pojawia się od razu po rozstrzygnięciu
  // wszystkich decydujących meczów, bez czekania na zakończenie rozgrywki.
  const podium = useMemo(
    () => podiumEntries(bracketView, standings, matches, names),
    [bracketView, standings, matches, names],
  );
  const roundNow = currentRoundLabel(bracketQuery.data?.bracket);

  /** Podpis rozgrywki w przełączniku: „Singiel • Open”. */
  function switcherSubtitle(item: PublicCompetitionSummary): string {
    return [
      MATCH_TYPE_LABELS[item.matchType] ?? item.matchType,
      item.category ? (COMPETITION_CATEGORY_LABELS[item.category] ?? item.category) : null,
    ]
      .filter(Boolean)
      .join(' • ');
  }

  return (
    <div className="min-h-dvh bg-canvas">
      {/* Pasek górny — sama tożsamość klubu, bez żadnego wejścia do aplikacji. */}
      {/* z-30 — pasek musi zostać nad całą treścią przy przewijaniu. Treść sięga
          maksymalnie z-10 (lepkie kolumny `ScheduleGrid`), więc przy równym z-10
          wygrywała kolejność w drzewie i wjeżdżała na pasek. Warstwy modalne
          (tło szuflady z-40, szuflada z-50, tooltip, toast) zostają nad paskiem. */}
      <header className="sticky top-0 z-30 border-b border-border bg-surface">
        {/* Wysoki pasek: sama wysokosc idzie z py-5, elementy zostaja w swoich
            rozmiarach; items-center pilnuje pionowego wysrodkowania. */}
        <div
          className={cn(
            'mx-auto flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-5 sm:px-6',
            containerWidth,
          )}
        >
          {/* Pasek nosi samo logo Tenify — nazwa imprezy stoi w nagłówku
              wybranego turnieju, więc tutaj tylko dublowała się z treścią. */}
          <Logo size="md" className="shrink-0" />
          {/* Odnosniki do mediow — te same ikony co na landingu w prawym gornym
              rogu. Instagram ma juz profil; Facebooka i TikToka podepniemy,
              gdy powstana. */}
          <nav className="ml-auto flex items-center gap-2" aria-label="Media społecznościowe">
            <a
              href="https://www.instagram.com/jointenify/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center"
              aria-label="Tenify na Instagramie"
            >
              <img src={socialInstagram} alt="" className="size-10" />
            </a>
            <a href="#" className="flex items-center" aria-label="Tenify na Facebooku">
              <img src={socialFacebook} alt="" className="size-10" />
            </a>
            <a href="#" className="flex items-center" aria-label="Tenify na TikToku">
              <img src={socialTiktok} alt="" className="size-10" />
            </a>
          </nav>
          {/* Przelacznik motywu celowo wylaczony — ekran prezentacji dziala
              wylacznie w motywie ciemnym (zolte logo i ikony sa pod niego
              projektowane). Odkomentuj, gdy wroci potrzeba obu motywow:
              <ThemeToggle /> */}
          {/* Na wąskich ekranach lista turniejów chowa się pod ten przycisk —
              na szerokich stoi na stałe w lewej kolumnie. */}
          {competitions.length > 0 && (
            <IconButton
              icon={<Menu />}
              label={t('publicScreen.openSwitcher')}
              variant="secondary"
              onClick={() => setMenuOpen(true)}
              className="lg:hidden"
            />
          )}
        </div>
      </header>

      <main className={cn('mx-auto flex flex-col gap-5 px-4 py-5 sm:px-6', containerWidth)}>
        {clubQuery.isError && <Alert tone="danger">{t('publicScreen.loadError')}</Alert>}
        {clubQuery.isPending && <Skeleton className="h-24 w-full" />}

        {clubQuery.isSuccess && competitions.length === 0 && (
          <EmptyState
            icon={<Trophy />}
            title={t('publicScreen.emptyTitle')}
            description={t('publicScreen.emptyDescription')}
            className="min-h-[24rem]"
          />
        )}

        {competitions.length > 0 && (
          <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:items-start lg:gap-6">
            {/* Tło szuflady — tylko wąskie ekrany; kliknięcie zamyka listę. */}
            {menuOpen && (
              <button
                type="button"
                aria-label={t('publicScreen.closeSwitcher')}
                onClick={() => setMenuOpen(false)}
                className="fixed inset-0 z-40 bg-overlay lg:hidden"
              />
            )}

            {/* Przełącznik turniejów — na szerokich ekranach stałe menu w lewej
                kolumnie, poniżej `lg` szuflada spod przycisku w pasku górnym. */}
            <nav
              aria-label={t('publicScreen.switcherLabel')}
              className={cn(
                'flex-col gap-1.5 lg:sticky lg:top-24 lg:z-auto lg:flex lg:w-auto lg:max-w-none lg:overflow-visible lg:bg-transparent lg:p-0 lg:shadow-none',
                menuOpen
                  ? 'fixed inset-y-0 right-0 z-50 flex w-72 max-w-[85vw] overflow-y-auto bg-surface p-4 shadow-xl'
                  : 'hidden',
              )}
            >
              <div className="mb-1 flex items-center justify-between gap-2 lg:hidden">
                <span className="text-[0.6875rem] font-bold tracking-[0.08em] text-fg-muted uppercase">
                  {t('publicScreen.switcherLabel')}
                </span>
                <IconButton
                  icon={<X />}
                  label={t('publicScreen.closeSwitcher')}
                  variant="secondary"
                  onClick={() => setMenuOpen(false)}
                />
              </div>

              {/* Info ogólne — pierwsza pozycja, domyślna po wejściu na ekran. */}
              <button
                type="button"
                aria-current={activeId === null ? 'true' : undefined}
                onClick={() => {
                  setPickedId(INFO_TAB);
                  setMenuOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-xl border px-3.5 py-2 text-left transition-colors',
                  activeId === null
                    ? 'border-brand-500 bg-brand-subtle'
                    : 'border-border bg-surface hover:bg-surface-hover',
                )}
              >
                <Home className="size-4 shrink-0 text-fg-muted" aria-hidden="true" />
                <span className="text-sm font-bold text-fg-strong">
                  {t('publicScreen.overview.tab')}
                </span>
              </button>

              {competitions.map((item) => {
                const selected = item.id === activeId;
                const itemProgress = screenProgress(
                  switcherMatches.byId.get(item.id)?.matches ?? [],
                );
                const champion = switcherChampions.get(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-current={selected ? 'true' : undefined}
                    // Wybór z szuflady od razu ją zamyka — na telefonie lista
                    // przykrywa treść, dla której się ją otwierało.
                    onClick={() => {
                      setPickedId(item.id);
                      setMenuOpen(false);
                    }}
                    className={cn(
                      'flex w-full flex-col items-start gap-1.5 rounded-xl border px-3.5 py-2.5 text-left transition-colors',
                      selected
                        ? 'border-brand-500 bg-brand-subtle'
                        : 'border-border bg-surface hover:bg-surface-hover',
                    )}
                  >
                    <span className="flex w-full items-start justify-between gap-2">
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-bold text-fg-strong">
                          {item.name}
                        </span>
                        <span className="text-xs text-fg-muted">{switcherSubtitle(item)}</span>
                      </span>
                      <Badge tone={COMPETITION_STATUS_TONE[item.status]} size="sm">
                        {COMPETITION_STATUS_LABELS[item.status] ?? item.status}
                      </Badge>
                    </span>
                    <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-fg-muted">
                      <span className="inline-flex items-center gap-1">
                        <Users className="size-3.5" aria-hidden="true" />
                        {item.participantCount}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="size-3.5" aria-hidden="true" />
                        {formatDateRange(item.startDate, item.endDate)}
                      </span>
                    </span>
                    {itemProgress.total > 0 && (
                      <span className="flex w-full items-center gap-2">
                        <ProgressBar
                          size="sm"
                          value={itemProgress.resolved}
                          max={itemProgress.total}
                          className="flex-1"
                        />
                        <span className="text-xs text-fg-subtle tabular-nums">
                          {itemProgress.resolved}/{itemProgress.total}
                        </span>
                      </span>
                    )}
                    {champion && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-text">
                        <Trophy className="size-3.5" aria-hidden="true" />
                        {t('publicScreen.overview.champion', { name: champion })}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Prawa kolumna — Info ogólne albo centrum wybranego turnieju. */}
            {activeId === null && clubQuery.data ? (
              <PublicClubOverview
                club={clubQuery.data}
                logo={screen?.logo ?? null}
                screenTitle={screen?.title ?? null}
                onShowTournaments={() => setMenuOpen(true)}
              />
            ) : (
              <div className="flex min-w-0 flex-col gap-5">
                {infoQuery.isError && (
                  <Alert tone="danger">{t('publicScreen.competitionLoadError')}</Alert>
                )}

                {/* Nagłówek turnieju */}
                {info ? (
                  <header className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      {/* Sama kategoria rozgrywki. Nazwy imprezy z oprawy ekranu tu
                      nie powtarzamy — nizej stoi wlasna nazwa turnieju. Dyscypliny
                      tez nie podajemy: impreza jest jednodyscyplinowa. */}
                      {info.category && (
                        <p className="mb-1 text-xs font-bold tracking-[0.08em] text-brand-text uppercase">
                          {COMPETITION_CATEGORY_LABELS[info.category] ?? info.category}
                        </p>
                      )}
                      <h1 className="truncate text-3xl font-extrabold tracking-[-0.03em] text-fg-strong">
                        {info.name}
                      </h1>
                      <p className="mt-1 text-sm text-fg-muted">{t('publicScreen.subtitle')}</p>
                    </div>
                    <Badge tone={COMPETITION_STATUS_TONE[info.status]}>
                      {COMPETITION_STATUS_LABELS[info.status] ?? info.status}
                    </Badge>
                  </header>
                ) : (
                  <Skeleton className="h-16 w-96" />
                )}

                {info && (
                  <Card padding="md">
                    <div className="flex items-center gap-5">
                      {/* Logo organizatora — to samo, ktore pasek boczny aplikacji
                      pokazuje na dole po lewej. Wersja zaszyta w buildzie
                      (oprawa ekranu) wygrywa z adresem z API. */}
                      <Avatar
                        src={screen?.logo ?? clubQuery.data?.clubLogoUrl ?? null}
                        name={clubQuery.data?.clubName ?? screen?.title ?? 'Klub'}
                        size="xl"
                        fit="contain"
                        className="shrink-0"
                      />
                      <MetaBar
                        className="min-w-0 flex-1"
                        items={[
                          ...(roundNow
                            ? [
                                {
                                  icon: <Trophy />,
                                  label: t('publicScreen.meta.round'),
                                  value: roundNow,
                                },
                              ]
                            : []),
                          ...(progress.total > 0
                            ? [
                                {
                                  icon: <LayoutGrid />,
                                  label: t('publicScreen.meta.progress'),
                                  value: (
                                    <span className="flex flex-col gap-1">
                                      {t('publicScreen.meta.progressValue', {
                                        resolved: progress.resolved,
                                        total: progress.total,
                                      })}
                                      <ProgressBar
                                        size="sm"
                                        value={progress.resolved}
                                        max={progress.total}
                                        className="w-32"
                                      />
                                    </span>
                                  ),
                                },
                              ]
                            : []),
                          {
                            icon: <Users />,
                            label: t('publicScreen.meta.players'),
                            value: info.participantCount,
                          },
                          ...(courtCount > 0
                            ? [
                                {
                                  icon: <LayoutGrid />,
                                  label: t('publicScreen.meta.courts'),
                                  value: courtCount,
                                },
                              ]
                            : []),
                        ]}
                      />
                    </div>
                  </Card>
                )}

                {/* Komunikat o braku drabinki zostaje tylko dla systemu pucharowego
                przed losowaniem — pozostale systemy pokazuja od razu grupy albo
                tabele ligi, bez zadnych komunikatow. */}
                {info && info.bracketSize === null && info.system === 'PLAYOFF' && (
                  <Alert tone="info">{t('publicScreen.notDrawn')}</Alert>
                )}

                {/* Podium po rozstrzygnięciu decydujących meczów — nad drabinką
                i wszystkimi sekcjami meczów; 3. miejsce tylko gdy jednoznaczne. */}
                {podium.length > 0 && (
                  <PodiumCard title={t('draw.result.podium')} entries={podium} />
                )}

                <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
                  {/* Drabinka */}
                  <div className="flex min-w-0 flex-col gap-5">
                    {/* Ostrzezenie o drabince tylko tam, gdzie drabinka istnieje —
                    formaty grupowe (grupa finalowa, klasyfikacja) jej nie maja,
                    a bledny wpis w cache (zbiorcze pobranie mistrzow) nie moze
                    straszyc komunikatem. */}
                    {info !== undefined && info.bracketSize !== null && bracketQuery.isError && (
                      <Alert tone="warning">{t('publicScreen.bracketError')}</Alert>
                    )}
                    {bracketQuery.isPending && bracketQuery.fetchStatus !== 'idle' && (
                      <Skeleton className="h-96 w-full" />
                    )}
                    {bracketView && (
                      <Card padding="lg" className="flex flex-col gap-4">
                        {/* Przy fazie grupowej drabinka JEST fazą finałową — mówimy
                        to wprost, żeby widz wiedział, na co patrzy. */}
                        <CardHeader
                          title={
                            groups.length > 0
                              ? t('publicScreen.sections.finalPhase')
                              : t('publicScreen.sections.bracket')
                          }
                          className="mb-0"
                        />
                        <Bracket
                          rounds={bracketView.rounds}
                          showAvatars
                          finaleCompact={!champion}
                          finale={
                            <WinnerCard
                              title={t('draw.result.champion')}
                              {...(champion ? { winnerName: champion } : {})}
                            />
                          }
                        />
                        {/* Mecz o 3. miejsce stoi poza siatka drabinki, wiec trzeba go
                        dorysowac osobno — inaczej znika z ekranu prezentacyjnego. */}
                        {bracketView.thirdPlace && (
                          <div className="flex flex-col gap-2">
                            <h3 className="text-[0.6875rem] font-bold tracking-[0.08em] text-fg-muted uppercase">
                              {t('draw.result.thirdPlace')}
                            </h3>
                            {/* Para deblowa zajmuje dwie linie — karta potrzebuje wyzszej ramki. */}
                            <div
                              className={
                                matchHasPair(bracketView.thirdPlace)
                                  ? 'h-28 max-w-[15rem]'
                                  : 'h-16 max-w-[15rem]'
                              }
                            >
                              <BracketMatchCard match={bracketView.thirdPlace} showAvatars />
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-fg-subtle">{t('draw.result.legend')}</p>
                      </Card>
                    )}

                    {/* Tabela ligi/challenge — te systemy nie mają drabinki, ranking
                    jest ich sednem i staje na miejscu drabinki. */}
                    {isLeague && leagueRows.length > 0 && (
                      <GroupStandingsCard
                        title={t('publicScreen.sections.table')}
                        rows={leagueRows}
                        matchesLabel={t('draw.result.matchesResolved', {
                          resolved: progress.resolved,
                          total: progress.total,
                        })}
                      />
                    )}

                    {/* Faza finałowa rozgrywana w grupie (secondPhaseType = FINAL_GROUP).
                    Tabela grupy 0 liczy się od zera — fazy się nie sumują. */}
                    {!isClassification && finalGroup && (
                      <GroupStandingsCard
                        title={t('publicScreen.sections.finalGroup')}
                        rows={buildGroupTable(0, finalGroup.members, standings, names, null)}
                        matchesLabel={t('draw.result.matchesResolved', {
                          resolved: finalGroup.matchesResolved,
                          total: finalGroup.matchesTotal,
                        })}
                      />
                    )}

                    {/* Grupy klasyfikacyjne: podium z grupy 1. miejsc, niżej kolejne miejsca. */}
                    {isClassification && (
                      <div className="flex flex-col gap-4">
                        {classificationGroups.map((group) => (
                          <GroupStandingsCard
                            key={group.groupNumber}
                            title={
                              group.rank === 1
                                ? t('draw.result.podiumGroup')
                                : t('draw.result.classificationGroup', { rank: group.rank })
                            }
                            rows={buildGroupTable(
                              group.groupNumber,
                              group.view.members,
                              standings,
                              names,
                              null,
                            )}
                            matchesLabel={t('draw.result.matchesResolved', {
                              resolved: group.view.matchesResolved,
                              total: group.view.matchesTotal,
                            })}
                          />
                        ))}
                      </div>
                    )}

                    {/* Faza grupowa — pod fazą finałową, bo to ona jest teraz bieżąca. */}
                    {groups.length > 0 && (
                      <div className="flex flex-col gap-3">
                        <h2 className="text-lg font-bold text-fg-strong">
                          {t('publicScreen.sections.groups')}
                        </h2>
                        {!bracketView && !finalGroup && groupMatchesLeft > 0 && (
                          <Alert tone="info">
                            {t('publicScreen.groupsPending', { count: groupMatchesLeft })}
                          </Alert>
                        )}
                        <div className="grid items-start gap-4 xl:grid-cols-2">
                          {groups.map((group) => (
                            <GroupStandingsCard
                              key={group.number}
                              title={t('draw.result.group', { number: group.number })}
                              rows={buildGroupTable(
                                group.number,
                                group.members,
                                standings,
                                names,
                                info?.advancingPerGroup ?? null,
                                extraCaptains,
                              )}
                              advancing={info?.advancingPerGroup ?? null}
                              extraQualifiers={info?.extraQualifiers ?? null}
                              matchesLabel={t('draw.result.matchesResolved', {
                                resolved: group.matchesResolved,
                                total: group.matchesTotal,
                              })}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Ostatnie wyniki */}
                    <Card>
                      <CardHeader title={t('publicScreen.sections.recent')} />
                      {matchesQuery.isPending ? (
                        <Skeleton className="h-40 w-full" />
                      ) : sections.recent.length === 0 ? (
                        <p className="text-sm text-fg-muted">{t('publicScreen.empty.recent')}</p>
                      ) : (
                        <div className="grid gap-x-8 divide-y divide-border lg:grid-cols-2 lg:divide-y-0">
                          {sections.recent.slice(0, 8).map((match) => (
                            <MatchScoreRow
                              key={match.id}
                              match={toBracketMatchView(match, names, NO_SEEDS)}
                              showAvatars
                              // Ptaszek i faza meczu w jednej linii nad para — w wąskiej
                              // kolumnie opis z boku zjadał miejsce nazwiskom i wynikowi.
                              metaPlacement="top"
                              className="lg:border-b lg:border-border"
                              meta={
                                <span>
                                  {/* Faza pucharowa w kolorze akcentu — na ekranie
                                  ogladanym z dystansu latwiej ja wtedy wychwycic. */}
                                  <span className={roundAccent(match.round) ?? undefined}>
                                    {roundLabel(match.round) ?? ''}
                                  </span>
                                  {match.courtId && courtNames.get(match.courtId) ? (
                                    <span className="text-fg-subtle">
                                      {' '}
                                      • {courtNames.get(match.courtId)}
                                    </span>
                                  ) : null}
                                </span>
                              }
                            />
                          ))}
                        </div>
                      )}
                    </Card>
                  </div>

                  {/* Prawa kolumna — mecze */}
                  <div className="flex flex-col gap-5">
                    <Card accent="brand">
                      <CardHeader
                        title={
                          sections.live.length > 0
                            ? `${t('publicScreen.sections.live')} (${sections.live.length})`
                            : t('publicScreen.sections.live')
                        }
                      />
                      {sections.live.length === 0 ? (
                        <p className="text-sm text-fg-muted">{t('publicScreen.empty.live')}</p>
                      ) : (
                        <div className="flex flex-col divide-y divide-border">
                          {sections.live.map((match) => {
                            const court = match.courtId ? courtNames.get(match.courtId) : undefined;
                            const round = roundLabel(match.round);
                            return (
                              <LiveMatchCard
                                key={match.id}
                                match={toBracketMatchView(match, names, NO_SEEDS)}
                                courtLabel={court ?? round ?? ''}
                                // Wynik wpisuje się dopiero po meczu, więc w trakcie
                                // kolumny setów pokazywałyby same myślniki. Widza
                                // interesuje za to, jak długo para jest już na korcie.
                                showScore={false}
                                startedAt={match.startedAt}
                                {...(court && round ? { roundLabel: round } : {})}
                              />
                            );
                          })}
                        </div>
                      )}
                    </Card>

                    {/* Plan następnych meczów istnieje na ekranie tylko wtedy, gdy
                    organizator go upublicznił — inaczej nie ma tu żadnej sekcji. */}
                    {schedulePublished && (
                      <Card>
                        <CardHeader title={t('publicScreen.sections.upcoming')} />
                        {sections.upcoming.length === 0 ? (
                          <p className="text-sm text-fg-muted">
                            {t('publicScreen.empty.upcoming')}
                          </p>
                        ) : (
                          <div className="flex flex-col divide-y divide-border">
                            {sections.upcoming.slice(0, 6).map((match) => (
                              <MatchScoreRow
                                key={match.id}
                                match={toBracketMatchView(match, names, NO_SEEDS)}
                                showAvatars
                                // Mecz jeszcze się nie odbył — kolumny setów pokazywałyby
                                // same myślniki i zabierały miejsce na termin i kort.
                                showScore={false}
                                meta={
                                  <UpcomingMatchMeta
                                    size="sm"
                                    time={shortTime(match.scheduledTime) ?? roundLabel(match.round)}
                                    place={
                                      match.courtId ? (courtNames.get(match.courtId) ?? null) : null
                                    }
                                  />
                                }
                              />
                            ))}
                          </div>
                        )}
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stopka strony, nie kolumny — stoi pod cala trescia i zostaje takze
            wtedy, gdy klub nie ma zadnych publicznych rozgrywek: odpytywanie
            chodzi niezaleznie od tego, czy jest co pokazac. */}
        <footer className="flex flex-col gap-1 border-t border-border pt-3 text-center text-xs text-fg-subtle">
          <span>{t('publicScreen.autoRefresh', { seconds: PUBLIC_SCREEN_REFRESH_MS / 1000 })}</span>
          <span>
            {t('publicScreen.footerLegal')}{' '}
            <a href="mailto:kontakt@tenify.pl" className="text-brand hover:underline">
              kontakt@tenify.pl
            </a>
          </span>
          {/* Przelacznik szerokosci — celowo dyskretny i na samym dole: ustawia
              sie go raz przy wieszaniu ekranu, a potem ma nie rozpraszac. */}
          <button
            type="button"
            onClick={toggleWideMode}
            aria-pressed={wideMode}
            className="mx-auto mt-1 inline-flex items-center gap-1.5 text-fg-subtle transition-colors hover:text-fg-muted"
          >
            {wideMode ? (
              <Minimize2 className="size-3.5" aria-hidden="true" />
            ) : (
              <Maximize2 className="size-3.5" aria-hidden="true" />
            )}
            {t(wideMode ? 'publicScreen.wideMode.disable' : 'publicScreen.wideMode.enable')}
          </button>
        </footer>
      </main>
    </div>
  );
}
